import React from 'react';
import type { Route } from './SimpleRouter';

interface BreadcrumbItem {
  label: string;
  icon?: string;
  route?: Route;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  onNavigate: (route: Route) => void;
}

/**
 * Breadcrumbs component for navigation
 * Shows the current location path and allows quick navigation back
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, onNavigate }) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <nav className="breadcrumbs flex items-center space-x-2 text-sm mb-4 px-4 py-2 bg-white border-b border-gray-200">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isClickable = !isLast && item.route;

        return (
          <React.Fragment key={index}>
            {isClickable ? (
              <button
                onClick={() => item.route && onNavigate(item.route)}
                className="flex items-center space-x-1 text-tg-link hover:underline transition-colors"
              >
                {item.icon && <span>{item.icon}</span>}
                <span>{item.label}</span>
              </button>
            ) : (
              <span className="flex items-center space-x-1 text-gray-900 font-medium">
                {item.icon && <span>{item.icon}</span>}
                <span>{item.label}</span>
              </span>
            )}
            
            {!isLast && (
              <svg 
                className="w-4 h-4 text-gray-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7" 
                />
              </svg>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

/**
 * Helper function to generate breadcrumbs for different routes
 */
export function getBreadcrumbs(currentRoute: Route, additionalContext?: string): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [];

  // Always start with home if not on home page
  if (currentRoute !== 'home') {
    breadcrumbs.push({
      label: 'Главная',
      icon: '🏠',
      route: 'home'
    });
  }

  // Add route-specific breadcrumbs
  switch (currentRoute) {
    case 'home':
      breadcrumbs.push({
        label: 'Главная',
        icon: '🏠'
      });
      break;

    case 'exercises':
      breadcrumbs.push({
        label: 'Упражнения',
        icon: '💪'
      });
      break;

    case 'programs':
      breadcrumbs.push({
        label: 'Программы',
        icon: '📋'
      });
      break;

    case 'workout':
      breadcrumbs.push({
        label: 'Тренировка',
        icon: '🏋️‍♂️'
      });
      break;

    case 'history':
      breadcrumbs.push({
        label: 'История',
        icon: '📊'
      });
      break;

    case 'analytics':
      breadcrumbs.push({
        label: 'Аналитика',
        icon: '📈'
      });
      break;

    case 'comments':
      breadcrumbs.push({
        label: 'Комментарии',
        icon: '💬'
      });
      break;

    case 'localization':
      breadcrumbs.push({
        label: 'Локализация',
        icon: '🌍'
      });
      break;

    case 'responsive-test':
      breadcrumbs.push({
        label: 'Тесты',
        icon: '🧪'
      });
      breadcrumbs.push({
        label: 'Адаптивность'
      });
      break;

    case 'workflow-test':
      breadcrumbs.push({
        label: 'Тесты',
        icon: '🧪'
      });
      breadcrumbs.push({
        label: 'Рабочие процессы'
      });
      break;

    case 'integration-test':
      breadcrumbs.push({
        label: 'Тесты',
        icon: '🧪'
      });
      breadcrumbs.push({
        label: 'Интеграция'
      });
      break;
  }

  // Add additional context if provided
  if (additionalContext) {
    breadcrumbs.push({
      label: additionalContext
    });
  }

  return breadcrumbs;
}

export default Breadcrumbs;
